import React from 'react';

import { mosaiqStyled, CommonProps } from '../../lib';
import { DropdownArrow, FieldPlaceholder } from '../../base';
import { Typography } from '../Typography';
import { OverflowMenu } from '../OverflowMenu';
import { Label } from '../Label';

import { SelectArrow, SelectContainer, SelectField, SelectValue } from './Select.styles';
import { SelectProps } from './types';
import { generateId } from '../../utils';

const Select = <T,>({
  label,
  helperText,
  autoSize = false,
  autoFocus = false,
  disabled = false,
  locked = false,
  error = false,
  required = false,
  fullWidth = false,
  placeholder = 'Select',
  placement = 'bottom-start',
  sameWidth = true,
  inline,
  startAdornment,
  endAdornment,
  value,
  renderValue,
  options = [],
  renderOption,
  closeOnSelect = true,
  appendTo,
  size,
  maxHeight,
  maxWidth,
  fieldProps,
  ...props
}: SelectProps<T> & CommonProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [inputId] = React.useState(generateId(label));
  const [focus, setFocus] = React.useState<boolean>(autoFocus);

  const enabled = !(disabled || locked);

  return (
    <>
      <OverflowMenu
        id="overflow-menu"
        appendTo={appendTo}
        disabled={!enabled}
        closeOnSelect={closeOnSelect}
        defaultOpen={autoFocus}
        onClose={() => {
          props.onBlur?.();
          setFocus(false);
        }}
        placement={placement}
        sameWidth={sameWidth}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        openerWidth={inline ? 'max-content' : fullWidth ? '100%' : 'auto'}
        opener={({ isOpen }) => (
          <SelectContainer fullWidth={fullWidth} {...props} onClick={() => setFocus(true)}>
            {!!label && (
              <label htmlFor={inputId}>
                <Label color={!enabled ? 'placeholder' : 'body'}>
                  {label} {required && <>*</>}
                </Label>
              </label>
            )}
            <SelectField
              role="listbox"
              ref={inputRef}
              size={size}
              autoSize={autoSize}
              disabled={!enabled}
              error={error}
              focus={focus}
              onClick={() => inputRef?.current?.focus()}
              {...fieldProps}
            >
              {startAdornment}
              <SelectValue>
                {value !== undefined && value !== null ? (
                  renderValue(value)
                ) : (
                  <FieldPlaceholder>{placeholder}</FieldPlaceholder>
                )}
              </SelectValue>
              {endAdornment}
              <SelectArrow>
                <DropdownArrow open={isOpen} locked={locked} size={size} />
              </SelectArrow>
            </SelectField>
          </SelectContainer>
        )}
      >
        {options.map(renderOption)}
      </OverflowMenu>
      {!!helperText && (
        <Typography
          variant="body4"
          marginTop={0.5}
          color={error ? 'error' : !enabled ? 'placeholder' : 'passive'}
        >
          {helperText}
        </Typography>
      )}
    </>
  );
};

export default mosaiqStyled(Select) as typeof Select;
